<template>
  <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
    <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
      <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      Upload File
    </h2>

    <!-- Drop Zone -->
    <div
      class="relative border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-200"
      :class="[
        isDragging
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50',
        isUploading ? 'pointer-events-none opacity-60' : '',
      ]"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInputRef"
        type="file"
        class="hidden"
        :disabled="isUploading"
        @change="onFileSelected"
      />

      <div v-if="!selectedFile">
        <svg
          class="w-12 h-12 mx-auto mb-3 transition-colors duration-200"
          :class="isDragging ? 'text-blue-500' : 'text-gray-400'"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p class="text-base font-medium text-gray-700 mb-1">
          {{ isDragging ? 'Drop your file here' : 'Drag & drop your file here' }}
        </p>
        <p class="text-sm text-gray-500">or <span class="text-blue-600 font-medium">browse</span> to choose</p>
        <p class="text-xs text-gray-400 mt-2">最大ファイルサイズ: 5GB</p>
      </div>

      <div v-else class="flex items-center justify-center gap-3">
        <div class="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div class="text-left">
          <p class="text-sm font-medium text-gray-800 truncate max-w-xs">{{ selectedFile.name }}</p>
          <p class="text-xs text-gray-500">{{ formatSize(selectedFile.size) }}</p>
        </div>
        <button
          class="ml-2 text-gray-400 hover:text-red-500 transition-colors"
          @click.stop="clearFile"
          title="Remove file"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Upload Progress -->
    <div v-if="isUploading" class="mt-4">
      <div class="flex justify-between items-center mb-1">
        <span class="text-sm font-medium text-gray-700">{{ uploadStatus }}</span>
        <span class="text-sm font-medium text-blue-600">{{ uploadProgress }}%</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div
          class="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
          :style="{ width: `${uploadProgress}%` }"
        ></div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
      <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-sm text-red-700">{{ errorMessage }}</p>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
      <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-sm text-green-700">{{ successMessage }}</p>
    </div>

    <!-- Optional Password -->
    <div class="mt-4">
      <label class="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5 cursor-pointer select-none">
        <input type="checkbox" v-model="usePassword" class="rounded border-gray-300 text-blue-600" />
        ダウンロードにパスワードを設定する
      </label>
      <div v-if="usePassword" class="relative">
        <input
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="パスワードを入力"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          @click="showPassword = !showPassword"
        >
          <svg v-if="showPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      </div>
      <p v-if="usePassword && password" class="text-xs text-amber-600 mt-1">
        共有リンクを開いた人はこのパスワードを入力する必要があります
      </p>
    </div>

    <!-- Upload Button -->
    <button
      class="mt-4 w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2"
      :class="[
        selectedFile && !isUploading
          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow'
          : 'bg-gray-100 text-gray-400 cursor-not-allowed',
      ]"
      :disabled="!selectedFile || isUploading"
      @click="startUpload"
    >
      <svg v-if="isUploading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      {{ isUploading ? 'Uploading…' : 'Upload File' }}
    </button>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { requestUploadUrl, uploadFileToS3 } from '../api.js';

const emit = defineEmits(['uploaded']);

const fileInputRef = ref(null);
const selectedFile = ref(null);
const isDragging = ref(false);
const isUploading = ref(false);
const uploadProgress = ref(0);
const uploadStatus = ref('');
const errorMessage = ref('');
const successMessage = ref('');
const usePassword = ref(false);
const password = ref('');
const showPassword = ref(false);
let dragCounter = 0;

function formatSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function triggerFileInput() {
  if (!isUploading.value) {
    fileInputRef.value?.click();
  }
}

function onFileSelected(event) {
  const file = event.target.files?.[0];
  if (file) {
    setFile(file);
  }
}

function setFile(file) {
  selectedFile.value = file;
  errorMessage.value = '';
  successMessage.value = '';
  uploadProgress.value = 0;
}

function clearFile() {
  selectedFile.value = null;
  errorMessage.value = '';
  successMessage.value = '';
  uploadProgress.value = 0;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
}

function onDragEnter() {
  dragCounter++;
  isDragging.value = true;
}

function onDragLeave() {
  dragCounter--;
  if (dragCounter === 0) {
    isDragging.value = false;
  }
}

function onDrop(event) {
  dragCounter = 0;
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    setFile(file);
  }
}

async function startUpload() {
  if (!selectedFile.value || isUploading.value) return;

  const file = selectedFile.value;
  isUploading.value = true;
  uploadProgress.value = 0;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    // Step 1: Get presigned URL (pass password if set)
    uploadStatus.value = 'Preparing upload…';
    const { uploadUrl, fileId, shareToken, fileName } = await requestUploadUrl(
      file.name,
      file.size,
      file.type || 'application/octet-stream',
      usePassword.value ? password.value : undefined
    );

    // Step 2: Upload directly to S3
    uploadStatus.value = 'Uploading to S3…';
    await uploadFileToS3(uploadUrl, file, (percent) => {
      uploadProgress.value = percent;
    });

    uploadProgress.value = 100;
    uploadStatus.value = 'Upload complete!';
    successMessage.value = `"${fileName}" uploaded successfully!`;

    // Clear selected file and notify parent
    clearFile();
    emit('uploaded', { fileId, shareToken, fileName });
  } catch (error) {
    errorMessage.value = error.message || 'Upload failed. Please try again.';
    uploadProgress.value = 0;
  } finally {
    isUploading.value = false;
    uploadStatus.value = '';
  }
}
</script>
